import { Injectable } from '@angular/core';
import { WorkflowStatus } from '@api/projects/interfaces';
import { InterpolationService } from '@app/services/interpolation/interpolation.service';
import { ProjectService } from '@app/services/project/project.service';
import { ReportDataResolver, ResolveReportDataService } from '@app/workflow/components/report-card/resolve-report-data.service';
import { IParticipant, IReportItem, ReportViewConfig } from '@interfaces/interfaces';
import { ListReportEntry, ListReportItem, ReportConfig } from '@interfaces/reports';
import { SessionItemData } from '@workflow/services/session-item-data';
import { isNil } from 'lodash';

export type Visibility = {
	visible: boolean;
	printable: boolean;
	disabled?: boolean;
};
export type ListReportEntryWithVisibility = ListReportEntry & Visibility;
export type ReportItemWithVisibility = IReportItem & Visibility;
export type ReportConfigWithVisibility = Omit<ReportConfig, 'reports'> & Visibility & {
	reports: ReportItemWithVisibility[];
};

@Injectable({
	providedIn: 'root'
})
export class ReportFilterService {
	constructor(
		private projectService: ProjectService,
		private interpolationService: InterpolationService,
		private resolveReportDataService: ResolveReportDataService,
	) {
	}

	public static isVisible(reportViewConfig: ReportViewConfig, data?: SessionItemData): boolean {
		if (isNil(reportViewConfig)) {
			return true;
		}

		if (reportViewConfig.visibleIf) {
			return !!data?.getProperty(reportViewConfig.visibleIf);
		}
		if (!isNil(reportViewConfig.isVisible)) {
			return !!reportViewConfig.isVisible;
		}
		return true;
	}

	public static isPrintable(reportViewConfig: ReportViewConfig, data?: SessionItemData): boolean {
		if (isNil(reportViewConfig)) {
			return true;
		}

		if (reportViewConfig.printableIf) {
			return !!data?.getProperty(reportViewConfig.printableIf);
		}
		if (!isNil(reportViewConfig.isPrintable)) {
			return !!reportViewConfig.isPrintable;
		}
		return true;
	}

	public async resolveVisible(reportViewConfig: ReportViewConfig, resolver: ReportDataResolver): Promise<boolean> {
		if (reportViewConfig?.visibleIf) {
			await resolver.resolveData(reportViewConfig?.visibleIf);
		}
		return ReportFilterService.isVisible(reportViewConfig, resolver.data);
	}

	public async resolvePrintable(reportViewConfig: ReportViewConfig, resolver: ReportDataResolver): Promise<boolean> {
		if (reportViewConfig?.printableIf) {
			await resolver.resolveData(reportViewConfig?.printableIf);
		}
		return ReportFilterService.isPrintable(reportViewConfig, resolver.data);
	}

	public async resolveVisibility(reportViewConfig: ReportViewConfig, resolver: ReportDataResolver): Promise<ReportViewConfig> {
		return {
			isVisible: await this.resolveVisible(reportViewConfig, resolver),
			isPrintable: await this.resolvePrintable(reportViewConfig, resolver),
		};
	}

	public async resolveReportConfigs(
		reportConfigs: ReportConfig[],
		participant: IParticipant,
		date: Date,
		source: 'details' | 'info',
		itemId: string | undefined,
		status: WorkflowStatus,
		resolver: ReportDataResolver,
		incompleteAsDisabled?: boolean,
	): Promise<ReportConfigWithVisibility[]> {
		const usedReportConfigs = new Array<ReportConfigWithVisibility>();
		for (const config of reportConfigs) {
			let disabled = false;
			if (status && !config.itemIds.some(id => status.status[id]?.completed)) {
				if (!incompleteAsDisabled) {
					continue;
				}
				else {
					disabled = true;
				}
			}

			if (itemId && !config.itemIds.includes(itemId)) {
				continue;
			}

			const reportViewConfig = source === 'details' ? config.reportViewConfigs?.itemDetailView : config.reportViewConfigs?.participantDetailView;
			const isVisible = await this.resolveVisible(reportViewConfig, resolver);
			const isPrintable = await this.resolvePrintable(reportViewConfig, resolver);

			if (!isVisible && !isPrintable) {
				continue;
			}

			const context = {
				participant: participant.data,
				project: this.projectService.activeProject,
				today: {
					localeDateString: date.toLocaleDateString(),
					now: Date.now(),
					year: date.getFullYear(),
					month: date.getMonth(),
					day: date.getDate(),
					hour: date.getHours(),
					minute: date.getMinutes(),
					dayName: date.toLocaleDateString().split(',')[0],
				},
				page: '<span class="page-counter"></span>',
				pageTotal: 10,
			};

			let reports = await Promise.all(config.reports?.map(async item => {
				const itemReportViewConfig = source === 'details' ? item.reportViewConfigs?.itemDetailView : item.reportViewConfigs?.participantDetailView;
				const visibility = await this.resolveVisibility(itemReportViewConfig, resolver);
				return ({...item, visible: visibility.isVisible, printable: visibility.isPrintable});
			}));
			reports = reports.filter(item => item.printable || item.visible);
			if (reports.length === 0) {
				continue;
			}
			for (const report of reports) {
				if (report.type === 'ListReport') {
					for (const entry of ((report as unknown as ListReportItem).entries) as ListReportEntryWithVisibility[]) {
						const entryReportViewConfig = source === 'details' ? entry.reportViewConfigs?.itemDetailView : entry.reportViewConfigs?.participantDetailView;
						const visibility = await this.resolveVisibility(entryReportViewConfig, resolver);
						entry.visible = visibility.isVisible;
						entry.printable = visibility.isPrintable;
					}
				}
			}

			usedReportConfigs.push({
				...config,
				reports: reports,
				visible: isVisible,
				printable: isPrintable,
				categories: config.categories || [],
				disabled: disabled,
				title: this.interpolationService.interpolate(config.title, context),
				header: this.interpolationService.interpolate(config.header, context),
				footer: this.interpolationService.interpolate(config.footer, context),
				description: this.interpolationService.interpolate(config.description, context),
			});
		}

		await this.resolveReportDataService.resolveRequiredData(usedReportConfigs, resolver);

		return usedReportConfigs;
	}
}
